import React, { useEffect, useState, useCallback } from "react";
import { adminClient } from "../../../lib/admin";
import {
  FileText, Search, CheckCircle2, XCircle, AlertCircle, Clock, ShieldCheck, Eye,
  Download, RefreshCw, ArrowLeft, MessageSquarePlus, SlidersHorizontal,
} from "lucide-react";

const STATUS_CLS = {
  draft: "bg-black/10 text-black/60", submitted: "bg-blue-100 text-blue-700",
  under_review: "bg-amber-100 text-amber-700", more_info_required: "bg-orange-100 text-orange-700",
  resubmitted: "bg-blue-100 text-blue-700", approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700", withdrawn: "bg-black/10 text-black/40",
  expired: "bg-black/10 text-black/40", suspended: "bg-red-100 text-red-700",
};
const PROGRAMS = ["seller", "dropshipping", "wholesale", "pro_ecommerce", "affiliate", "brand_partner", "creator", "instructor"];
const STATUSES = Object.keys(STATUS_CLS);

export default function AdminApplications() {
  const c = adminClient();
  const [list, setList] = useState([]); const [counts, setCounts] = useState({});
  const [filters, setFilters] = useState({ program: "", status: "", q: "" });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docUrls, setDocUrls] = useState({});
  const [note, setNote] = useState(""); const [msg, setMsg] = useState(""); const [reason, setReason] = useState("");
  const [analytics, setAnalytics] = useState(null); const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    const params = {}; Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await c.get("/admin/applications", { params });
    setList(data.applications); setCounts(data.counts);
    try { setAnalytics((await c.get("/admin/applications/analytics")).data); } catch { /* */ }
    try { setSettings((await c.get("/admin/applications/settings")).data); } catch { /* */ }
  }, [filters]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    const { data } = await c.get(`/admin/applications/${id}`); setDetail(data.application); setDocUrls({});
    setNote(""); setMsg(""); setReason("");
  };
  const refresh = async () => { if (detail) await open(detail.id); await load(); };

  const act = async (action, body = {}) => {
    setBusy(true);
    try { await c.post(`/admin/applications/${detail.id}/${action}`, body); await refresh(); }
    catch (e) { alert(e?.response?.data?.detail?.reason || "Action failed"); }
    finally { setBusy(false); }
  };

  const viewDoc = async (d) => {
    try {
      const res = await c.get(`/admin/applications/${detail.id}`); // ensure fresh token
      const token = (res.data.application.documents.find((x) => x.doc_id === d.doc_id) || {}).download_token;
      const r = await c.get(`/applications/${detail.id}/documents/${d.doc_id}`, { params: { token }, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      setDocUrls((u) => ({ ...u, [d.doc_id]: url })); window.open(url, "_blank");
    } catch { alert("Could not open document"); }
  };

  const exportApp = async () => {
    const { data } = await c.get(`/admin/applications/${detail.id}/export`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `TAVEN_Application_${detail.id.slice(0, 8)}.json`; a.click(); window.URL.revokeObjectURL(url);
  };

  // ---------- Detail ----------
  if (detail) {
    const d = detail; const esign = d.esign || {};
    return (
      <div data-testid="admin-app-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-black/50 hover:text-taven-orange mb-4"><ArrowLeft className="w-4 h-4" /> Back to queue</button>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl font-black font-display">{d.config?.name} — {d.personal?.full_name || d.member_email}</h2>
            <p className="text-xs text-black/40">ID {d.id.slice(0, 8)} • {d.member_email}</p>
          </div>
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_CLS[d.status]}`} data-testid="admin-app-status">{d.status.replace(/_/g, " ")}</span>
        </div>

        {!d.terms_current && <div className="border border-red-300 bg-red-50 p-3 mb-4 text-sm text-red-700 flex items-center gap-2" data-testid="terms-mismatch"><AlertCircle className="w-4 h-4" /> Accepted terms do not match current legal versions — re-acceptance required.</div>}

        <div className="grid md:grid-cols-2 gap-5">
          <Card title="Applicant Profile">
            <KV label="Name" v={d.personal?.full_name} /><KV label="Email" v={d.member_email} />
            <KV label="Phone" v={d.personal?.phone} /><KV label="Country" v={d.personal?.country} />
            <KV label="Address" v={`${d.personal?.address || ""}, ${d.personal?.city || ""} ${d.personal?.state || ""} ${d.personal?.postal_code || ""}`} />
            {d.business?.business_name && <><KV label="Business" v={d.business?.business_name} /><KV label="Type" v={d.business?.business_type} /><KV label="Tax ID" v={d.business?.tax_id} /></>}
          </Card>
          <Card title="Program Details">
            {Object.entries(d.answers || {}).map(([k, v]) => <KV key={k} label={k.replace(/_/g, " ")} v={v} />)}
            <div className="mt-3 flex gap-2">
              <SelBadge label="Identity" v={d.identity_status} onSet={(val) => c.post(`/admin/applications/${d.id}/verify`, { identity_status: val }).then(refresh)} opts={["pending", "verified", "rejected"]} />
              <SelBadge label="Business" v={d.business_status} onSet={(val) => c.post(`/admin/applications/${d.id}/verify`, { business_status: val }).then(refresh)} opts={["pending", "verified", "rejected", "not_required"]} />
            </div>
          </Card>
          <Card title="Documents">
            {(d.documents || []).length === 0 ? <p className="text-sm text-black/40">No documents.</p> :
              d.documents.map((x) => (
                <div key={x.doc_id} className="flex items-center justify-between py-1.5 border-b border-black/5">
                  <span className="text-sm"><FileText className="w-3.5 h-3.5 inline mr-1 text-taven-gold" />{x.label} <span className="text-black/30 text-xs">({x.filename})</span></span>
                  <button onClick={() => viewDoc(x)} className="text-xs font-bold uppercase text-taven-orange hover:underline flex items-center gap-1" data-testid={`admin-viewdoc-${x.key}`}><Eye className="w-3.5 h-3.5" /> View</button>
                </div>
              ))}
          </Card>
          <Card title="Terms & Signature">
            {esign.completed ? (
              <>
                <KV label="Signed by" v={esign.name} /><KV label="Method" v={esign.method} />
                <KV label="Signed at" v={esign.signed_at ? new Date(esign.signed_at).toLocaleString() : "—"} />
                <KV label="IP" v={esign.ip} /><KV label="Device" v={(esign.device_fingerprint || "").slice(0, 24)} />
                <KV label="Language" v={esign.language} /><KV label="Terms version" v={esign.terms_version} />
                <KV label="Acceptance hash" v={(esign.acceptance_hash || "").slice(0, 20) + "…"} />
                <div className="mt-2 text-xs text-black/50">{(esign.terms || []).length} legal documents accepted</div>
              </>
            ) : <p className="text-sm text-black/40">Not signed.</p>}
          </Card>
        </div>

        {/* Decision controls */}
        <Card title="Review Actions">
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={() => act("review")} disabled={busy} className="px-3 py-2 text-xs font-bold uppercase border border-black/15 hover:bg-taven-gray" data-testid="admin-act-review">Start Review</button>
            <button onClick={() => act("approve", { reason: reason || "Meets requirements" })} disabled={busy} className="px-3 py-2 text-xs font-bold uppercase bg-green-600 text-white hover:bg-green-700" data-testid="admin-act-approve">Approve</button>
            <button onClick={() => { if (!reason.trim()) return alert("Enter a reason (visible to applicant)"); act("reject", { reason }); }} disabled={busy} className="px-3 py-2 text-xs font-bold uppercase bg-red-600 text-white hover:bg-red-700" data-testid="admin-act-reject">Reject</button>
            <button onClick={() => act("suspend", { reason: reason || "Compliance hold" })} disabled={busy} className="px-3 py-2 text-xs font-bold uppercase border border-red-300 text-red-700 hover:bg-red-50" data-testid="admin-act-suspend">Suspend</button>
            <button onClick={() => act("expire")} disabled={busy} className="px-3 py-2 text-xs font-bold uppercase border border-black/15 hover:bg-taven-gray" data-testid="admin-act-expire">Expire</button>
            <button onClick={() => act("reopen")} disabled={busy} className="px-3 py-2 text-xs font-bold uppercase border border-black/15 hover:bg-taven-gray" data-testid="admin-act-reopen">Reopen</button>
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required for reject; shown to applicant)" className="w-full border border-black/15 px-3 py-2 text-sm mb-3" data-testid="admin-reason" />
          <div className="flex gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Request more information message…" className="flex-1 border border-black/15 px-3 py-2 text-sm" data-testid="admin-info-msg" />
            <button onClick={() => { if (!msg.trim()) return; act("request-info", { message: msg }); }} disabled={busy} className="px-3 py-2 text-xs font-bold uppercase border border-orange-300 text-orange-700 hover:bg-orange-50 flex items-center gap-1" data-testid="admin-act-requestinfo"><MessageSquarePlus className="w-3.5 h-3.5" /> Request Info</button>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 gap-5">
          <Card title="Internal Notes (staff only)">
            <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
              {(d.admin_notes || []).map((n, i) => <p key={i} className="text-xs border-l-2 border-taven-gold pl-2">{n.note}<br /><span className="text-black/30">{n.by} • {new Date(n.at).toLocaleString()}</span></p>)}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note…" className="flex-1 border border-black/15 px-3 py-2 text-sm" data-testid="admin-note-input" />
              <button onClick={async () => { if (!note.trim()) return; await c.post(`/admin/applications/${d.id}/note`, { note }); setNote(""); refresh(); }} className="px-3 py-2 text-xs font-bold uppercase border border-black/15 hover:bg-taven-gray" data-testid="admin-note-add">Add</button>
            </div>
          </Card>
          <Card title="Audit Timeline">
            <button onClick={exportApp} className="mb-3 inline-flex items-center gap-1 text-xs font-bold uppercase border border-black/15 px-3 py-1.5 hover:bg-taven-gray" data-testid="admin-export"><Download className="w-3.5 h-3.5" /> Export Record</button>
            <ol className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="admin-audit">
              {(d.audit || []).map((a, i) => <li key={i} className="text-xs"><span className="font-bold">{a.action}</span> — {a.actor_email} <span className="text-black/30">{new Date(a.at).toLocaleString()}</span></li>)}
            </ol>
          </Card>
        </div>
      </div>
    );
  }

  // ---------- Queue ----------
  return (
    <div data-testid="admin-applications">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black font-display flex items-center gap-2"><FileText className="w-5 h-5 text-taven-gold" /> Applications</h2>
        <button onClick={load} className="text-xs font-bold uppercase flex items-center gap-1 text-black/50 hover:text-taven-orange"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="app-analytics">
          <Stat label="Total" v={analytics.total} />
          <Stat label="Open Drafts" v={analytics.open_drafts} />
          <Stat label="Approval Rate" v={analytics.approval_rate_pct != null ? `${analytics.approval_rate_pct}%` : "—"} />
          <Stat label="Avg Decision" v={analytics.avg_time_to_decision_hours != null ? `${analytics.avg_time_to_decision_hours}h` : "—"} />
          <Stat label="Approved" v={analytics.funnel?.approved || 0} />
        </div>
      )}
      {analytics?.by_program && (
        <div className="border border-black/10 bg-white p-3 mb-4 text-xs overflow-x-auto" data-testid="app-analytics-program">
          <span className="font-bold uppercase tracking-wide text-black/50 mr-3">By program:</span>
          {Object.entries(analytics.by_program).map(([p, v]) => <span key={p} className="mr-3">{p}: <strong>{v.total}</strong> ({v.approved || 0}✓)</span>)}
        </div>
      )}
      <div className="mb-4">
        <button onClick={() => setShowSettings((s) => !s)} className="text-xs font-bold uppercase text-black/50 hover:text-taven-orange flex items-center gap-1" data-testid="app-settings-toggle"><SlidersHorizontal className="w-3.5 h-3.5" /> Draft Expiration Settings</button>
        {showSettings && settings && (
          <div className="border border-black/10 bg-white p-4 mt-2 flex flex-wrap items-end gap-4" data-testid="app-settings-form">
            <label className="text-xs">Remind after (days)<input type="number" min={1} defaultValue={settings.reminder_after_days} onChange={(e) => setSettings((s) => ({ ...s, reminder_after_days: +e.target.value }))} className="block mt-1 border border-black/15 px-3 py-2 text-sm w-24" data-testid="setting-reminder" /></label>
            <label className="text-xs">Expire drafts after (days)<input type="number" min={1} defaultValue={settings.draft_expiry_days} onChange={(e) => setSettings((s) => ({ ...s, draft_expiry_days: +e.target.value }))} className="block mt-1 border border-black/15 px-3 py-2 text-sm w-24" data-testid="setting-expiry" /></label>
            <button onClick={async () => { await c.put("/admin/applications/settings", { draft_expiry_days: settings.draft_expiry_days, reminder_after_days: settings.reminder_after_days }); load(); }} className="px-4 py-2 text-xs font-bold uppercase border border-black/15 hover:bg-taven-gray" data-testid="setting-save">Save</button>
            <button onClick={async () => { const r = await c.post("/admin/applications/expire-sweep"); alert(`Sweep: ${r.data.reminders_sent} reminders, ${r.data.expired} expired`); load(); }} className="px-4 py-2 text-xs font-bold uppercase border border-black/15 hover:bg-taven-gray" data-testid="setting-sweep">Run Sweep Now</button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        {STATUSES.map((s) => <span key={s} className={`px-2 py-1 rounded-full ${STATUS_CLS[s]}`}>{s.replace(/_/g, " ")}: <strong>{counts[s] || 0}</strong></span>)}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={filters.program} onChange={(e) => setFilters((f) => ({ ...f, program: e.target.value }))} className="border border-black/15 px-3 py-2 text-sm" data-testid="filter-program">
          <option value="">All programs</option>{PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="border border-black/15 px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center border border-black/15 px-2"><Search className="w-4 h-4 text-black/30" /><input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Name / email / business" className="px-2 py-2 text-sm outline-none" data-testid="filter-q" /></div>
      </div>
      <div className="overflow-x-auto border border-black/10">
        <table className="w-full text-sm" data-testid="admin-app-table">
          <thead className="bg-taven-gray/50 text-left text-xs uppercase tracking-wide"><tr><th className="p-3">Applicant</th><th className="p-3">Program</th><th className="p-3">Country</th><th className="p-3">Status</th><th className="p-3">Signed</th><th className="p-3">Updated</th><th className="p-3"></th></tr></thead>
          <tbody>
            {list.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-black/40">No applications.</td></tr> :
              list.map((a) => (
                <tr key={a.id} className="border-t border-black/5 hover:bg-taven-gray/30">
                  <td className="p-3"><div className="font-bold">{a.applicant || "—"}</div><div className="text-xs text-black/40">{a.email}</div></td>
                  <td className="p-3">{a.program}</td>
                  <td className="p-3">{a.country || "—"}</td>
                  <td className="p-3"><span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_CLS[a.status]}`}>{a.status.replace(/_/g, " ")}</span></td>
                  <td className="p-3">{a.esigned ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-black/30" />}</td>
                  <td className="p-3 text-xs text-black/40">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3"><button onClick={() => open(a.id)} className="text-xs font-bold uppercase text-taven-orange hover:underline" data-testid={`admin-open-${a.id}`}>Review</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="border border-black/10 bg-white p-4 mb-5">
    <h3 className="font-black font-display uppercase text-xs tracking-wide mb-3 text-black/70">{title}</h3>
    {children}
  </div>
);
const KV = ({ label, v }) => (
  <div className="flex justify-between py-1 border-b border-black/5 text-sm"><span className="text-black/50 capitalize">{label}</span><span className="font-medium text-right">{v || "—"}</span></div>
);
const SelBadge = ({ label, v, onSet, opts }) => (
  <label className="text-xs">{label}: <select value={v || ""} onChange={(e) => onSet(e.target.value)} className="border border-black/15 px-1.5 py-1 text-xs"><option value="">—</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
);
const Stat = ({ label, v }) => (
  <div className="border border-black/10 bg-white p-3 text-center"><p className="text-2xl font-black font-display">{v}</p><p className="text-[10px] uppercase tracking-wide text-black/40">{label}</p></div>
);
